import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { RuleEditorComponent } from '@app/shared/components/rule-editor/rule-editor.component';
import { NotificationType } from '@app/shared/interfaces';
import { OptimizelyService } from '@app/shared/services/optimizely/optimizely.service';
import { RuleService } from '@app/shared/services/rule/rule.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { DialogDeleteRulesetComponent } from '../../components/dialog-delete-ruleset/dialog-delete-ruleset.component';

@Component({
  standalone: false,
  selector: 'sbnb-review-detail',
  templateUrl: './review-detail.component.html',
  styleUrls: ['./review-detail.component.scss'],
})
export class ReviewDetailComponent implements OnInit {
  ruleSetId: any;
  ruleSetData: any;
  loading: boolean;
  saveStatus: number;
  lastSaved: Date;
  timingsMeta: any;

  @ViewChild('ruleEditor') ruleEditor: RuleEditorComponent;

  // Add new properties for tags
  private selectedTags: { [category: string]: Set<string> } = {
    cleanliness: new Set(),
    respect_house_rules: new Set(),
    communication: new Set(),
  };

  public tagsEnabled = false;
  public ratingsEnabled = false;

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private ruleSetService: RuleService,
    public dialog: MatDialog,
    private titleService: TitleService,
    private router: Router,
    private toast: ToastNotificationsService,
    private optimizelyService: OptimizelyService
  ) {
    this.optimizelyService.reviewCategoryTagsEnabled$.subscribe(enabled => {
      this.tagsEnabled = enabled;
      this.ratingsEnabled = enabled;
    });
  }

  ngOnInit() {
    this.titleService.manuallySet('Review detail');
    this.loading = true;

    this.route.params.subscribe((params) => {
      this.ruleSetId = params['id'];
      this.ruleSetService.getRuleSetById(this.ruleSetId).subscribe((res) => {
        this.ruleSetData = res.data;
        this.timingsMeta = res.meta?.timings;

        // Initialize category tags from metadata if they exist
        if (this.ruleSetData.metadata?.category_tags) {
          this.ruleSetData.category_tags = this.ruleSetData.metadata.category_tags;
        }

        // Initialize grades if they exist (as first-class property)
        if (!this.ruleSetData.grades) {
          this.ruleSetData.grades = {
            cleanliness: 5,
            respect_house_rules: 5,
            communication: 5
          };
        }

        this.loading = false;
        this.titleService.manuallySet(this.ruleSetData.name);
      });
    });
  }

  goBack() {
    this.router.navigate(['/gx/reviews']);
  }

  updateRuleSet() {
    this.saveStatus = 1;

    // Ensure metadata exists
    if (!this.ruleSetData.metadata) {
      this.ruleSetData.metadata = {};
    }

    // Only include tags in metadata if feature is enabled
    if (this.tagsEnabled) {
      this.ruleSetData.metadata.category_tags = this.ruleSetData.category_tags;
    }

    this.ruleSetService.updateRuleSet(this.ruleSetId, this.ruleSetData).subscribe((res: any) => {
      if (res.error) {
        this.saveStatus = -1;
      } else {
        this.saveStatus = 2;
        this.lastSaved = new Date();
      }
    });
  }

  deleteRulesetPrompt() {
    const dialogRef = this.dialog.open(DialogDeleteRulesetComponent, {
      width: '720px',
      data: {
        item: 'review',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteRuleset();
      }
    });
  }

  deleteRuleset() {
    this.ruleSetService.deleteRuleset(this.ruleSetId).subscribe((res) => {
      this.location.back();
    });
  }

  ruleSaveStatusChange($event) {
    this.saveStatus = $event;

    if ($event === 2) {
      this.lastSaved = new Date();
    }
  }

  copyRuleset() {
    this.ruleSetService.copyRuleset(this.ruleSetId).subscribe((res) => {
      if (res.data.id) {
        this.router.navigate(['/gx/reviews/review/', res.data.id]);
      }
    });
  }

  handleScopeChange(event) {
    this.ruleSetData.all_platforms = event.all_platforms;
    this.ruleSetData.all_hosts = event.all_hosts;
    this.ruleSetData.all_properties = event.all_properties;
    this.ruleSetData.properties = event.properties;
    this.ruleSetData.hosts = event.hosts;
    this.ruleSetData.platforms = event.platforms;

    this.updateRuleSet();

    setTimeout(() => {
      this.ruleEditor.getImportableData();
    }, 3000);
  }

  timingUpdated($event) {
    this.updateRuleSet();
  }

  activateOrDeactivateRule(event: MatCheckboxChange) {
    this.ruleSetData.active = event.checked;

    this.ruleSetService.activeOrDeactivateRuleset(this.ruleSetId, this.ruleSetData.active).subscribe((res: any) => {
      if (res.success) {
        this.saveStatus = 2;
        setTimeout(() => {
          this.saveStatus = null;
        }, 2500);
        return;
      }

      if (res.error) {
        this.ruleSetData.active = !this.ruleSetData.active;
        this.toast.open(`Oops! We ran into an error updating the Review rule`, 'Dismiss', NotificationType.Error);
      }
    });
  }

  updateGrades(field: string, value: number) {
    if (!this.ruleSetData.grades) {
      this.ruleSetData.grades = {};
    }
    this.ruleSetData.grades[field] = value;
    this.updateRuleSet();
  }

  getSelectedTags(category: string): string[] {
    if (!this.ruleSetData.category_tags?.[`${category}_tags`]) {
      return [];
    }
    return this.ruleSetData.category_tags[`${category}_tags`];
  }

  updateTags(category: string, selectedTags: string[]) {
    if (!this.ruleSetData.category_tags) {
      this.ruleSetData.category_tags = {
        cleanliness_tags: [],
        respect_house_rules_tags: [],
        communication_tags: [],
      };
    }
    this.ruleSetData.category_tags[`${category}_tags`] = selectedTags;
    this.updateRuleSet();
  }
}
